// Shrink a photo to a sane upload size BEFORE it leaves the browser.
//
// WHY THIS EXISTS (2026-08-19). An agent adding a listing manually watched every photo fail
// with "ატვირთვა ვერ შესრულდა". Nothing in the app was broken: nginx has no
// `client_max_body_size`, so its 1 MB DEFAULT 413'd the request at the proxy — a 2.18 MB and a
// 7.59 MB phone photo, both refused before node ever saw them, so there was no app-side log to
// find. The proxy limit is being raised separately; this module is the OTHER half, and it is
// worth having on its own merits: a modern phone camera writes 4-12 MB per shot, we render
// those at card-strip and gallery sizes, and shipping the original over a Georgian mobile
// connection costs the agent minutes per listing for pixels no surface displays.
//
// ⚠️ TWO RULES THIS MODULE WILL NOT BREAK:
//
//  1. IT NEVER TOUCHES A DOCUMENT. A cadastral extract, a PDF, a scan of a passport page — those
//     are EVIDENCE, and re-encoding evidence destroys exactly the fine text someone will later
//     need to read. Only `kind === 'photo'` reaches here, and only real raster images at that.
//
//  2. IT NEVER RETURNS SOMETHING BIGGER, AND NEVER RETURNS GARBAGE. Every failure path — no
//     canvas, a decode error, an already-small file, a re-encode that came out LARGER (which
//     genuinely happens: JPEG-encoding a flat PNG screenshot can inflate it) — returns the
//     ORIGINAL file untouched. A downscaler that can lose a photo is worse than no downscaler.
//
// Quality choice: long edge 2560 px at JPEG q0.85. 2560 is above every surface we render
// (the gallery lightbox is the largest and is viewport-bound) and above the 1920-2048 that
// the portals themselves publish, so a downscaled photo is still the best copy in the system.
// A 12 MP phone shot lands around 700 KB-1.2 MB at these settings — visually indistinguishable
// at 100%, roughly an order of magnitude off the wire.

export const MAX_EDGE = 2560;
export const JPEG_QUALITY = 0.85;
// Below this, re-encoding buys nothing and only risks a generation loss. A 1 MB photo is
// already fine to send once the proxy limit is raised.
export const SKIP_UNDER_BYTES = 1024 * 1024;

// The agent picks the size, the way a mail client asks before sending an attachment. The
// default is `large`, NOT `original`: the reported failure was an agent losing photos to a
// silent proxy refusal, and a default that keeps doing that is not a default. `original` stays
// available and honest — some listings really do want the full file — but it is a deliberate
// choice, not what happens to you.
//
// Edges are chosen against what we actually render: the card strip is ~400px, the gallery
// lightbox is viewport-bound (~1600px on a large desktop), and the portals themselves publish
// 1920-2048. So `medium` is already lossless-looking everywhere in this app and `large` is
// above every surface we have.
export const PHOTO_SIZES = [
  { key: 'small',    edge: 1280, quality: 0.82 },
  { key: 'medium',   edge: 1920, quality: 0.85 },
  { key: 'large',    edge: MAX_EDGE, quality: JPEG_QUALITY },
  { key: 'original', edge: null, quality: null },
];
export const DEFAULT_PHOTO_SIZE = 'large';

export function photoSizeSpec(key) {
  return PHOTO_SIZES.find(s => s.key === key) || PHOTO_SIZES.find(s => s.key === DEFAULT_PHOTO_SIZE);
}

const RESIZABLE = /^image\/(jpeg|png|webp|avif)$/i;

// ── iPhone HEIC (2026-10-21) ─────────────────────────────────────────────────────────────────
// An agent on a MacBook in Chrome picked 10 iPhone photos: every preview tile was a broken image
// and the size label could not shrink a byte (≈29.4 MB, no "was" figure). Chrome cannot decode
// HEIC at all — not in <img>, not in createImageBitmap — and the old rule here ("fall back to the
// original, the server accepts image/heic") would have stored .heic files that no Chrome surface
// (Desk, site) can show. So a HEIC becomes a JPEG IN THE BROWSER before anything else touches it,
// with heic2any (libheif in wasm), loaded only when a HEIC actually shows up — ~1 MB no other
// upload ever pays for. Then it is an ordinary JPEG: preview, size estimate, shrink, upload.
// ⚠️ Some systems hand a .heic over with an EMPTY type — the extension decides then, or a photo
// filter on `type.startsWith('image/')` drops it without a word.
// ⚠️ Read the date/GPS from the HEIC BEFORE converting (exifRead.js readPhotoMeta reads HEIC):
// the JPEG that comes out carries no metadata, like every canvas re-encode.
export function isHeic(file) {
  const type = String(file?.type || '').toLowerCase();
  if (/^image\/hei[cf](-sequence)?$/.test(type)) return true;
  return !type && /\.(heic|heif)$/i.test(String(file?.name || ''));
}

/**
 * A HEIC as a JPEG File (same name, .jpg); anything else, or a HEIC that cannot be converted,
 * comes back untouched — a photo is never lost here.
 */
export async function heicToJpeg(file) {
  if (!isHeic(file)) return file;
  try {
    const { default: heic2any } = await import('heic2any');
    const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    const blob = Array.isArray(out) ? out[0] : out;   // a multi-frame HEIC gives several — the first is the photo
    if (!blob?.size) return file;
    return new File([blob], String(file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg',
      { type: 'image/jpeg', lastModified: file.lastModified });
  } catch (e) {
    console.warn('[photo] HEIC could not be converted, sending the original:', e?.message || e);
    return file;
  }
}

/**
 * @param {File} file
 * @param {string} [sizeKey] one of PHOTO_SIZES; 'original' returns the file untouched.
 * @returns {Promise<{ file: File, resized: boolean, from?: number, to?: number }>}
 *          `file` is either a smaller JPEG or the original, never anything else.
 */
export async function downscalePhoto(file, sizeKey = DEFAULT_PHOTO_SIZE) {
  // a HEIC is a JPEG from here on, even at 'original' — a stored .heic is a photo nobody on Chrome can see
  if (isHeic(file)) file = await heicToJpeg(file);
  const keep = { file, resized: false };
  const spec = photoSizeSpec(sizeKey);
  if (!spec.edge) return keep;                       // 'original' — the agent asked for the file as-is
  if (!file || !RESIZABLE.test(file.type || '')) return keep;
  if (file.size <= SKIP_UNDER_BYTES) return keep;
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return keep;

  let bmp;
  try {
    bmp = await createImageBitmap(file);
  } catch {
    return keep; // undecodable here (corrupt header, a HEIC heic2any could not convert) — let the server have it
  }

  try {
    const long = Math.max(bmp.width, bmp.height);
    // Even at native size, a re-encode is worth it for a multi-megabyte original — that IS the
    // reported case (a 7.6 MB shot). Scale of 1 with a q0.85 JPEG still sheds most of the bytes.
    const scale = long > spec.edge ? spec.edge / long : 1;
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return keep;
    ctx.drawImage(bmp, 0, 0, w, h);

    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', spec.quality));
    // toBlob hands back null when the canvas is tainted or the encoder refuses the size.
    if (!blob || blob.size >= file.size) return keep;

    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return {
      file: new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified }),
      resized: true, from: file.size, to: blob.size,
    };
  } catch {
    return keep;
  } finally {
    bmp.close?.();
  }
}

/**
 * What these files will actually weigh at `sizeKey` — by encoding them, not by guessing a ratio.
 * A guess would be wrong in both directions (a flat interior photo compresses far better than a
 * detailed exterior), and the whole point of showing the number is that the agent can trust it.
 *
 * The cost is one encode pass per photo per selection change, which is why the caller debounces
 * and passes an AbortSignal — switching between four options with twenty photos queued must not
 * leave four stale passes racing to write the same label.
 *
 * @returns {Promise<{ bytes: number, original: number }>}
 */
export async function estimateTotal(files, sizeKey, signal) {
  let bytes = 0, original = 0;
  for (const f of files) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    original += f.size;
    const { file } = await downscalePhoto(f, sizeKey);
    bytes += file.size;
  }
  return { bytes, original };
}
